import { ApolloError, ApolloQueryResult } from '@apollo/client';
import { capitalize } from 'lodash';
import defaultTo from 'lodash/defaultTo';
import get from 'lodash/get';
import React, { useCallback } from 'react';
import {
  ColGroup,
  CustomSearch,
  ExpandableRow,
  HeadCell,
  OpenSidebarButton,
  SearchResultColumn,
  TableBody,
  TableHead,
} from '@css-xp/base';
import { CustomDynamicResultTable } from '@css-xp/base/custom-dynamic-result-table';
import { PaymentMethod } from '@css-xp/graphtypes';
import {
  CommerceAddress,
  Maybe,
  ShipToPartyAggregated,
  ShipToPartyAggregatedConnection,
  ShipToPartyConnection,
  ShipToPartyPageParams,
  ShipToPartySearchParams,
} from '@css-xp/graphtypes/src/generated-types-new';
import { routePaths } from '@css-xp/ppc-constants';
import { getCompanyNameRoute, getRouteUrl } from '@css-xp/ppc-utils';
import { DEFAULT_ROWS_PER_PAGE } from '@atlassian/cssxp-utils-entitlement';
import { emptyStateProp, errorStateProp } from '@css-xp/ppc-common';

import './sold-to-address-table.scss';

export enum SIDEBAR {
  ADD_SOLD_TO_ADDRESS = 'addSoldToAddress',
  EDIT_SOLD_TO_ADDRESS = 'editSoldToAddress',
  VIEW_LINKED_SUBSCRIPTIONS = 'viewLinkedSubscriptions',
}

export type AvailableShipToParties = {
  results: CommerceAddress[] | null;
  cursor?: string | null;
};

export interface SoldToAddressTableProps {
  addressResponse?: Maybe<ShipToPartyConnection> | Maybe<ShipToPartyAggregatedConnection>;
  loading: boolean;
  transactionAccountId: string;
  refetch: (
    _variables:
      | Partial<{
          searchParams: ShipToPartySearchParams;
          pageParams: ShipToPartyPageParams;
        }>
      | undefined
  ) => Promise<ApolloQueryResult<unknown>>;
  billToAddress?: CommerceAddress | null;
  availablePaymentMethods?: Maybe<Maybe<PaymentMethod>[]> | undefined;
  hidePricingEligibility?: boolean;
  showAddAddress?: boolean;
  currentPage?: number;
  rowsPerPage?: number;
  onRowChange?: (_arg0: number) => void;
  onPageChange?: (_arg0: number) => void;
  hideActions?: boolean;
  showSearchBox?: boolean;
  hideEdit?: boolean;
  onSearch?: (_value: string) => void;
  hideAddFromBillToAddress?: boolean;
  isGroupedData?: boolean;
  showAddAddressInRow?: boolean;
  error?: string | ApolloError;
  heading?: React.ReactNode;
}

export type ColumnList = HeadCell & SearchResultColumn & { id: string };

export interface FormData {
  searchTerm: string;
}

export const formatAddressLabel = (node: CommerceAddress) => {
  let formatAddressLabel1 = '';
  let formatAddressLabel2 = '';
  if (node?.addressLine1) {
    formatAddressLabel1 += node.addressLine1;
  }
  if (node?.addressLine2) {
    formatAddressLabel1 += `, ${node.addressLine2}`;
  }
  if (node?.city) {
    formatAddressLabel2 += `${node.city}`;
  }
  if (node?.postcode) {
    formatAddressLabel2 += `- ${node.postcode}`;
  }
  if (node?.state) {
    formatAddressLabel2 += `, ${node.state}`;
  }
  if (node?.country) {
    formatAddressLabel2 += `, ${node.country}`;
  }
  return (
    <div>
      {formatAddressLabel1}
      <br></br>
      {formatAddressLabel2}
    </div>
  );
};

export const getTaxExemptStatus = (item: CommerceAddress) => {
  const taxExemptionStatus = item?.taxExempt ? 'Yes' : 'No';
  if (item.taxLabels && item.taxLabels.length > 0) {
    const taxIds = item?.taxIds ?? [];
    const taxIdLabels = item.taxLabels[0]?.taxIdLabels;
    const displayName = taxIdLabels?.filter((tax) => tax?.displayName);
    return displayName?.length
      ? displayName?.reduce((prevVal, tax) => {
          const taxId = taxIds.filter((taxId) => taxId?.label === tax?.type);
          const taxExemption = taxId.length > 0 ? taxId[0]?.taxExempt : Boolean(item?.taxExempt);
          const taxExemptionStatus = taxExemption ? 'Yes' : 'No';
          return `${prevVal} ${tax?.displayName} (${taxExemptionStatus}) `;
        }, '')
      : 'No';
  }
  return taxExemptionStatus;
};
export const sidebarType = 'customer address';

export const getHeadersForTable = (hideActions: boolean, hideEdit: boolean) => {
  const companyNameHeader = {
    id: 'companyName',
    name: 'Customer',
    customClassNames: 'col-20',
  };

  const addressHeader = {
    name: 'Sold-to address',
    id: 'address',
    customClassNames: 'col-30',
  };

  const pricingEligibilityHeader = {
    name: 'Pricing plan eligibility',
    id: 'pricingEligibility',
  };
  const taxExemptHeader = {
    name: 'Tax exempt',
    id: 'taxExempt',
    customClassNames: hideActions ? 'col-15' : '',
  };
  const actionsHeader = {
    name: 'Actions',
    id: 'actions',
  };
  const editHeader = {
    name: '',
    id: 'edit',
  };
  const addHeader = {
    name: 'Actions',
    id: 'add',
  };

  const headers: any = [companyNameHeader, addressHeader, pricingEligibilityHeader, taxExemptHeader];

  if (!hideActions) {
    headers.push(actionsHeader);
  }
  if (!hideEdit) {
    headers.push(editHeader);
  }

  return headers;
};

export const extractPriceEligibility = (item: any) =>
  item?.priceEligibility ? capitalize(item?.priceEligibility).replace('_', ' ') : 'Commercial';

export const SearchSoldToAddressTable = ({
  addressResponse,
  loading,
  transactionAccountId,
  billToAddress,
  rowsPerPage,
  currentPage,
  availablePaymentMethods,
  hidePricingEligibility = false,
  showAddAddress = false,
  hideActions = false,
  showSearchBox = true,
  onRowChange,
  onPageChange,
  onSearch,
  refetch,
  hideEdit = false,
  hideAddFromBillToAddress,
  isGroupedData = false,
  showAddAddressInRow = false,
  error,
  heading,
}: SoldToAddressTableProps) => {
  const totalCount = addressResponse?.totalCount || 0;
  const shipToParties = addressResponse?.nodes;

  const renderActionsOnTop = useCallback(() => {
    return [
      () =>
        showAddAddress ? (
          <div className="add-sold-to-address add">
            <div className="add-action">
              <OpenSidebarButton
                sidebar={SIDEBAR.ADD_SOLD_TO_ADDRESS}
                sidebarOptions={{
                  sidebarWidth: '600px',
                  isOverlay: true,
                }}
                payload={{
                  transactionAccountId,
                  billToAddress,
                  refetch,
                  hidePricingEligibility,
                  sidebarType,
                  hideAddFromBillToAddress,
                  contentString: {
                    HEADING: 'Add customer',
                    SUBMIT_BUTTON: 'Add customer',
                    NOTIFICATION_SUCCESS_HEADING: 'Customer added',
                  },
                }}
                appearance="default"
                testId="add-sold-to-address"
                className="add-customer-btn"
              >
                Add new customer
              </OpenSidebarButton>
            </div>
          </div>
        ) : (
          <div />
        ),
    ];
  }, [transactionAccountId, billToAddress]);

  const _onSearch = useCallback(
    async (formData: FormData) => {
      const searchInput = formData?.searchTerm ?? '';
      if (onSearch) {
        onSearch(searchInput);
      }
    },
    [refetch]
  );

  const renderFilters = (showSearchBox: boolean) => {
    // To improve: Move filters into a separate component
    if (!showSearchBox) {
      return <div />;
    }
    return (
      <div className="sold-to-table-filter">
        <CustomSearch
          placeholder="Search"
          onSearch={_onSearch}
          isLoading={loading}
          isClearable={true}
          helperMessage="Enter company name or sold-to address"
        />
      </div>
    );
  };

  const getColumns = useCallback(
    (hideActions: boolean, hideEdit: boolean) => {
      return getHeadersForTable(hideActions, hideEdit);
    },
    [hideActions, hideEdit, showAddAddressInRow]
  );

  const getColumnData = (visibleColumns: ColumnList[], item: any, isSecondaryRow = false) => {
    const customerAddressPath = getRouteUrl(routePaths.customerAddressDetailsById, {
      txaId: transactionAccountId,
      addressId: item.id,
    });
    const companyNamePath = getCompanyNameRoute({ txaId: transactionAccountId, companyName: item.name });
    return visibleColumns.map((column) => {
      switch (column.id) {
        case 'companyName':
          return (
            <td>
              <a href={companyNamePath} className="company-name">
                {item?.name ?? 'Not available'}
              </a>
            </td>
          );
        case 'address':
          return (
            <td>
              <a href={customerAddressPath}>{formatAddressLabel(item)}</a>
            </td>
          );
        case 'pricingEligibility':
          return <td>{extractPriceEligibility(item)}</td>;
        case 'taxExempt':
          return <td>{getTaxExemptStatus(item)}</td>;
        case 'actions':
          return (
            <td>
              {item?.hasLinkedSubscriptions && (
                <OpenSidebarButton
                  sidebar={SIDEBAR.VIEW_LINKED_SUBSCRIPTIONS}
                  payload={{
                    shipToAddress: item,
                    transactionAccountId,
                    availablePaymentMethods: availablePaymentMethods,
                  }}
                  appearance="link"
                  sidebarOptions={{
                    sidebarWidth: '968px',
                    isOverlay: true,
                  }}
                  testId="view-linked-subscriptions-action"
                  spacing="compact"
                  className="view-subscriptions-link"
                >
                  View linked subscriptions and invoices
                </OpenSidebarButton>
              )}
            </td>
          );
        case 'edit':
          return (
            <>
              {isSecondaryRow ? <td /> : null}
              <td>
                {!hideEdit ? (
                  <OpenSidebarButton
                    sidebar={SIDEBAR.EDIT_SOLD_TO_ADDRESS}
                    payload={{
                      shipToAddress: item,
                      transactionAccountId,
                      billToAddress,
                      pricingEligibility: item?.shipToPriceEligibility,
                      refetch,
                      hidePricingEligibility,
                      sidebarType,
                    }}
                    appearance="link"
                    sidebarOptions={{
                      isOverlay: true,
                      sidebarWidth: '600px',
                    }}
                    testId="edit-ship-to-party-action"
                    spacing="none"
                  >
                    Edit
                  </OpenSidebarButton>
                ) : (
                  <div />
                )}
              </td>
            </>
          );
      }
    });
  };

  const hasNoResults = totalCount === 0;

  const renderRowWithGroupedData = (index: number, shipToPartyGroup: ShipToPartyAggregated) => {
    if (shipToPartyGroup?.data?.length === 1) {
      return (
        <tr
          data-testid={`ship-to-table-row-${index}`}
          key={`ship-to-table-row-${index}-${shipToPartyGroup?.key}`}
          className="ungrouped-child"
        >
          {getColumnData(getColumns(hideActions, hideEdit), shipToPartyGroup.data[0])}
        </tr>
      );
    }
    const companyNamePath = shipToPartyGroup?.key
      ? getCompanyNameRoute({ txaId: transactionAccountId, companyName: shipToPartyGroup.key })
      : '';
    // FIXME :: The current expandable-row component doesn't support colspan for columns
    const rows = [
      [
        <td key="1">
          <a href={companyNamePath} className="company-name">
            {shipToPartyGroup?.key ?? 'Not available'}
          </a>
        </td>,
        <td key="2" />,
        <td key="3" />,
        <td key="4" />,
      ],
    ];
    const subRows = defaultTo(get(shipToPartyGroup, 'data'), []).map((shipToParty) => {
      return (
        <tr
          data-testid={`ship-to-table-row-${shipToParty?.id}`}
          key={`ship-to-table-row-${index}-${shipToParty?.id}`}
          className="grouped-child"
        >
          {getColumnData(getColumns(hideActions, hideEdit), shipToParty, true)}
        </tr>
      );
    });
    return <ExpandableRow key={index} rowsAsCells={rows} subRows={subRows?.length > 0 && subRows} />;
  };

  const renderRowWithUnGroupedData = (index: number, shipToParty: CommerceAddress) => {
    return (
      <tr data-testid={`ship-to-table-row-${index}`} key={`ship-to-table-row-${index}-${shipToParty?.id}`}>
        {getColumnData(getColumns(hideActions, hideEdit), shipToParty)}
      </tr>
    );
  };

  const renderTableBody = (
    shipToParties: Maybe<CommerceAddress>[] | Maybe<ShipToPartyAggregated>[] | null | undefined
  ) => {
    return (shipToParties ?? [])?.map((item, index) => {
      if (isGroupedData) {
        return renderRowWithGroupedData(index, item as ShipToPartyAggregated);
      } else {
        return renderRowWithUnGroupedData(index, item as CommerceAddress);
      }
    });
  };

  return (
    <div className="sold-to-address-container">
      <CustomDynamicResultTable
        innerHeading={heading}
        testId="shipping-details"
        isLoading={loading}
        totalCount={totalCount}
        noResults={hasNoResults}
        isPaginationEnabled={totalCount > DEFAULT_ROWS_PER_PAGE}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowChange={onRowChange}
        selectableResults={true}
        error={error}
        actions={renderActionsOnTop()}
        boxShadow={true}
        className="sold-to-address-table"
        filters={renderFilters(showSearchBox)}
        shouldForceEnableFilters={true}
        emptyStateProps={emptyStateProp}
        errorStateProps={errorStateProp}
      >
        <ColGroup columns={getColumns(hideActions, hideEdit)} />
        <TableHead columns={getColumns(hideActions, hideEdit)} />
        <TableBody>{renderTableBody(shipToParties)}</TableBody>
      </CustomDynamicResultTable>
    </div>
  );
};
